/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  LoadingContainer,
  MapDisplay,
  MapPartnerTable,
  SectionHeader,
  SegmentedControl,
  StatsList,
  StatsListMetricItem,
  ProgramBlock,
  ExpandableItem,
  Modal,
  ImageCarousel,
  SpotlightVideoPlayer,
} from 'components'
import UpdateInfoNote from './update-info-note'
import NotContributorInfoNote from './not-contributor-info-note'
import PartnerTabs from './partner-tabs'
import { chunk, useCommunity } from 'utils'
import * as Types from 'types'
import { Spinner } from 'lp-components'
import { Link } from 'react-router'
import { ArrowUpIcon, ArrowDownIcon, InstructorsIcon } from 'components/icons'
import imagePlaceholder from 'images/image-placeholder.jpeg'
import scrollToHash from 'utils/local/scroll-to-hash'
import { isEmpty } from 'lodash'
import { TEACHING_ARTIST_ROUTE } from 'config'

const propTypes = {
  isLoading: PropTypes.bool.isRequired,
  partner: Types.partner,
  partnerLocation: Types.location,
  partnerLocations: PropTypes.arrayOf(Types.location),
  selectedSchoolYearId: PropTypes.number,
  programTypes: PropTypes.arrayOf(Types.programType).isRequired,
}

const defaultProps = {
  partner: null,
}

function PartnerContent({
  isLoading,
  partner,
  partnerLocations,
  partnerLocation,
  selectedSchoolYearId,
  programTypes,
}) {
  if (!partner) return <Spinner />

  const community = useCommunity()
  const {
    partnerProgramsEnabled,
    teachingArtist,
    subdomain,
    partnerSupportEmail,
  } = community
  const spotlightTranscriptRequestEmail =
    partnerSupportEmail || 'info@artlookmap.com'
  const partnerPhotoVideoEnabled = community?.partnerPhotoVideoEnabled
  const partnerProgramPhotoEnabled = community?.partnerProgramPhotoEnabled

  const [mapShown, setMapShown] = useState(false)
  const [mapArtistShown, setMapArtistShown] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)

  // Handle hash scrolling when component mounts
  useEffect(() => {
    const location = window.location
    scrollToHash({ location })
  }, [])

  const { disciplines, partnerships, programs } = partner
  const artists = partnerships.filter((p) => !!p.teachingArtistId)

  const hasPartnerships = partnerships.length > 0
  const hasPrograms = programs.length > 0 && partnerProgramsEnabled
  const hasArtists = !isEmpty(artists)
  const hasSpotlightVideo =
    !!partner.spotlightVideoUrl && !!partnerPhotoVideoEnabled
  const spotlightTranscriptText = (
    partner.spotlightVideoTranscript || ''
  ).trim()
  const hasSpotlightTranscript = spotlightTranscriptText.length > 0
  const spotlightCaptionsUrl = partner.spotlightVideoCaptionsUrl || ''
  const spotlightCaptionWords = partner.spotlightVideoCaptionWords
  const spotlightMediaAltText = (
    partner.spotlightVideoMediaAlternative || ''
  ).trim()
  const spotlightMediaAltId = `spotlight-video-media-alt-${partner.id}`
  const hasSpotlightMediaAlternative = spotlightMediaAltText.length > 0
  const hasSpotlightImages =
    !isEmpty(partner.spotlightImages) && !!partnerPhotoVideoEnabled
  const programGroups = chunk(programs, 2)
  const programNames = programTypes.map(
    (programType) => programType.displayName
  )

  const artistLocations = artists.map((artist) => artist.address)

  function ArtistHeader({ artist }) {
    return (
      <React.Fragment>
        <div className="image-block" aria-hidden>
          <div className="image-thumbnail-circle">
            <img src={artist.artistProfilePic || imagePlaceholder} alt="" />
          </div>
        </div>
        <div className="section-header-details">
          <div className="artist-title">
            <Link
              to={`/teaching-artist/${artist.teachingArtistId}`}
              className="section-header-title"
            >
              {artist.artistName}
            </Link>
            <p>{artist.artistRole}</p>
          </div>
          {artist.artistState === 'pending' ? (
            <span className="pending-approval">Pending Approval</span>
          ) : null}
        </div>
        {artist?.partnerPrograms?.length > 0 && (
          <>
            <div className="arrow-icon-container arrow-icon-up">
              <ArrowUpIcon />
            </div>
            <div className="arrow-icon-container arrow-icon-down">
              <ArrowDownIcon />
            </div>
          </>
        )}
      </React.Fragment>
    )
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      {/* Side Nav */}
      <PartnerTabs hasSchools={hasPartnerships} hasPrograms={hasPrograms} />

      {/* Note about updating info if missing a description or non contributor */}
      <>
        {subdomain === 'swpa' &&
        !(partner?.badges || []).some(
          (b) => b.name === 'org_contributor' && b.active
        ) ? (
          <NotContributorInfoNote />
        ) : !partner?.detail?.description ? (
          <UpdateInfoNote />
        ) : null}
      </>

      {/* Spotlight Video */}
      {hasSpotlightVideo && (
        <section name="spotlight-video">
          <div className="content-block-container spotlight-video-section">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="33"
                    viewBox="0 0 38 33"
                    fill="none"
                  >
                    <path
                      d="M1.5 6.72448V26.2821C1.5 30.2877 5.85293 32.8013 9.32712 30.7986L17.8082 25.9142L26.2894 21.0095C29.7636 19.0067 29.7636 13.9998 26.2894 11.997L17.8082 7.09232L9.32712 2.20805C5.85293 0.205281 1.5 2.69851 1.5 6.72448Z"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M36.5 28.7613V3.50195"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <h2>Spotlight Video</h2>
            </SectionHeader>
            <div className="row spotlight-video-content">
              <div className="col-6 spotlight-video-text">
                <h3>Welcome To {partner.name}</h3>
                {partner.spotlightVideoCaption && (
                  <p>{partner.spotlightVideoCaption}</p>
                )}
              </div>
              <div className="col-6 spotlight-video-thumbnail-col">
                <button
                  className="spotlight-video-thumbnail-btn"
                  onClick={() => setShowVideoModal(true)}
                  aria-label={`Play spotlight video for ${partner.name}`}
                >
                  <span className="spotlight-video-thumbnail-wrap">
                    <img
                      src={partner.spotlightVideoUrl
                        .replace('/video/upload/', '/video/upload/so_0/')
                        .replace(/\.mp4(\?.*)?$/, '.jpg')}
                      alt={`${partner.name} spotlight video thumbnail`}
                      className="spotlight-video-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <span
                      className="spotlight-video-thumbnail-play"
                      aria-hidden="true"
                    >
                      <span className="spotlight-video-thumbnail-play-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="28"
                          viewBox="0 0 29 40"
                          fill="none"
                        >
                          <path
                            d="M0.5 36.8261V2.93137C0.5 0.946106 2.77372 -0.216001 4.37016 0.994527L26.9622 17.9419C28.2684 18.9103 28.2684 20.8472 26.9622 21.8156L4.37016 38.763C2.77372 39.9735 0.5 38.8114 0.5 36.8261Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          {showVideoModal && (
            <Modal
              onClose={() => {
                setShowTranscriptModal(false)
                setShowVideoModal(false)
              }}
              className="spotlight-video-modal"
            >
              <SpotlightVideoPlayer
                videoSrc={partner.spotlightVideoUrl}
                captionsUrl={spotlightCaptionsUrl}
                captionWords={spotlightCaptionWords}
                videoAriaLabel={`Spotlight video for ${partner.name}`}
                ariaDescribedBy={
                  hasSpotlightMediaAlternative ? spotlightMediaAltId : undefined
                }
                videoStyle={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  display: 'block',
                }}
              />
              {hasSpotlightMediaAlternative ? (
                <section
                  id={spotlightMediaAltId}
                  className="spotlight-video-media-alternative"
                  tabIndex={-1}
                >
                  <h3 className="spotlight-video-media-alternative-heading">
                    Text version of this video
                  </h3>
                  <div
                    className="spotlight-video-media-alternative-body"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {spotlightMediaAltText}
                  </div>
                </section>
              ) : null}
              <div className="spotlight-video-transcript">
                {hasSpotlightTranscript ? (
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => setShowTranscriptModal(true)}
                    aria-label={`View transcript for ${partner.name} spotlight video`}
                  >
                    View Transcript
                  </button>
                ) : (
                  <div
                    className="spotlight-video-equivalent-info"
                    role="region"
                    aria-label="Video description and transcript information"
                  >
                    {partner.spotlightVideoCaption && (
                      <React.Fragment>
                        <h3 className="spotlight-video-equivalent-heading">
                          About this video
                        </h3>
                        <p>{partner.spotlightVideoCaption}</p>
                      </React.Fragment>
                    )}
                    <p>
                      A full text transcript is not published with this
                      spotlight video.
                    </p>
                    <p>
                      For a transcript or a text description of the audio, email{' '}
                      <a href={`mailto:${spotlightTranscriptRequestEmail}`}>
                        {spotlightTranscriptRequestEmail}
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </Modal>
          )}
          {showTranscriptModal && hasSpotlightTranscript && (
            <Modal
              onClose={() => setShowTranscriptModal(false)}
              className="spotlight-transcript-modal"
            >
              <h2 className="spotlight-transcript-modal-title">Transcript</h2>
              <div className="spotlight-transcript-modal-body">
                {spotlightTranscriptText}
              </div>
            </Modal>
          )}
        </section>
      )}

      {/* Spotlight Images */}
      {hasSpotlightImages && (
        <section name="spotlight-images">
          <div className="content-block-container spotlight-images-section">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
              </div>
              <h2>Spotlight Images</h2>
            </SectionHeader>
            <ImageCarousel
              autoPlay
              images={partner.spotlightImages}
              imagesPerPage={4}
              modalImagesPerPage={2}
            />
          </div>
        </section>
      )}

      {/* Data Dashboard */}
      <section name="data-dashboard" className="data-dashboard">
        <div className="content-block-container extra-padding">
          <SectionHeader>
            <div className="image-block">
              <svg width="84px" height="84px" viewBox="0 0 84 84" version="1.1">
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-247.000000, -570.000000)">
                    <g transform="translate(247.000000, 570.000000)">
                      <circle fill="#1F4B80" cx="42" cy="42" r="42"></circle>
                      <g
                        transform="translate(21.666667, 25.466667)"
                        fill="#FFFFFF"
                        fillRule="nonzero"
                      >
                        <path
                          d="M40.6875,26.6666667 L5.25,26.6666667 L5.25,1.33333333 C5.25,0.596666667 4.66265625,0 3.9375,0 L1.3125,0 C0.58734375,0 0,0.596666667 0,1.33333333 L0,29.3333333 C0,30.8058333 1.17550781,32 2.625,32 L40.6875,32 C41.4126562,32 42,31.4033333 42,30.6666667 L42,28 C42,27.2633333 41.4126562,26.6666667 40.6875,26.6666667 Z M38.0625,2.66666667 L28.3778906,2.66666667 C26.6240625,2.66666667 25.7455078,4.82083333 26.9858203,6.08083333 L29.6436328,8.78083333 L23.625,14.8958333 L17.6063672,8.78166667 C16.5809766,7.74 14.9190234,7.74 13.8944531,8.78166667 L8.25972656,14.5058333 C7.74703125,15.0266667 7.74703125,15.8708333 8.25972656,16.3916667 L10.1152734,18.2766667 C10.6279687,18.7975 11.4589453,18.7975 11.9716406,18.2766667 L15.75,14.4375 L21.7686328,20.5516667 C22.7940234,21.5933333 24.4559766,21.5933333 25.4805469,20.5516667 L33.3555469,12.5516667 L36.0133594,15.2516667 C37.2536719,16.5116667 39.3741805,15.6191667 39.3741805,13.8375 L39.3741805,4 C39.375,3.26333333 38.7876562,2.66666667 38.0625,2.66666667 Z"
                          id="Shape"
                        ></path>
                      </g>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
            <h2>Data Dashboard</h2>
          </SectionHeader>
          <StatsList>
            <StatsListMetricItem
              stat={disciplines.length}
              label="Arts Disciplines Offered"
              hideExpand={disciplines.length < 1}
              categoryValue="disciplines"
            >
              <ul>
                {disciplines.map((discipline) => (
                  <li key={discipline.id}>{discipline.displayName}</li>
                ))}
              </ul>
            </StatsListMetricItem>
            <StatsListMetricItem
              stat={partnerships.length}
              label="School Partners"
              hideExpand={partnerships.length < 1}
              categoryValue="partnerships"
            >
              <ul>
                {partnerships.map((partner) => (
                  <li key={partner.id}>{partner.schoolName}</li>
                ))}
              </ul>
            </StatsListMetricItem>
            {partnerProgramsEnabled && (
              <StatsListMetricItem
                stat={programs.length}
                label="Programs"
                hideExpand={programs.length < 1}
                categoryValue="programs"
              >
                <ul>
                  {programs.map((program) => (
                    <li key={program.id}>{program.name}</li>
                  ))}
                </ul>
              </StatsListMetricItem>
            )}
          </StatsList>
        </div>
      </section>

      {/* School Partners */}
      {hasPartnerships && (
        <section
          name="partners"
          className="content-block-container extra-padding partners-block"
        >
          <SectionHeader>
            <div className="image-block">
              <svg width="84px" height="84px" viewBox="0 0 84 84" version="1.1">
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g fillRule="nonzero">
                    <circle fill="#4A4A4A" cx="42" cy="42" r="42"></circle>
                    <path
                      d="M16.4554494,50.9995504 C15.6487086,50.9791825 14.8998748,50.5916639 14.4374619,49.9552498 C13.6863911,48.9019416 13.9249889,47.4670452 14.980114,46.6918101 L21.1866973,42.3514354 C22.3043638,41.6978453 23.7563066,41.9797038 24.5231933,42.9991331 C25.2900801,44.0185624 25.1148009,45.4337841 24.1204102,46.2512458 L17.9307847,50.5753033 C17.4968565,50.8609723 16.9811229,51.0092766 16.4554494,50.9995504 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M21.486855,56.998622 C20.4119749,57.0361723 19.444201,56.3029897 19.1154712,55.2020686 C18.7867413,54.1011476 19.1767312,52.899323 20.0710825,52.2571788 L26.0270912,47.6207122 C26.7165182,46.9874383 27.678344,46.8241658 28.5157063,47.1982632 C29.3530687,47.5723605 29.9240278,48.4204151 29.9929981,49.3925081 C30.0619685,50.3646012 29.6172591,51.2959549 28.842363,51.8022802 L22.9026276,56.4387468 C22.4975211,56.7814032 22.0019235,56.9773901 21.486855,56.998622 L21.486855,56.998622 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M27.5024563,61.999287 C26.4185698,62.0019299 25.4567969,61.3311217 25.1221296,60.3390794 C24.7874622,59.3470372 25.1564042,58.2605378 26.0352373,57.6500628 L32.2076758,53.349524 C33.3191936,52.6994887 34.7631486,52.9798142 35.5258164,53.9936988 C36.2884842,55.0075834 36.1141694,56.4151076 35.1252491,57.2281231 L29.0539982,61.528662 C28.605356,61.84675 28.05956,62.0123049 27.5024563,61.999287 L27.5024563,61.999287 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M33.4516746,67 C32.3893255,66.9931469 31.4507267,66.3320156 31.1215923,65.358727 C30.7924579,64.3854384 31.1466504,63.318413 32.0008793,62.709824 L38.1042248,58.4517909 C38.8131902,57.9552324 39.743232,57.8608371 40.5440126,58.204163 C41.3447931,58.5474889 41.8946548,59.2763765 41.9864699,60.1162609 C42.0782851,60.9561453 41.6981049,61.7794282 40.9891395,62.2759867 L34.885794,66.5500879 C34.4697623,66.8435493 33.9672698,67.0011889 33.4516746,67 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M64.4770833,36.8010897 L62.7805546,32.6160773 C61.8541221,30.0898202 59.9768107,28.0407898 57.5618157,26.9199766 C55.1468208,25.7991635 52.392077,25.6984303 49.9039022,26.6399485 L41.8284259,29.4988459 L40.7256823,29.9121804 L40.7256823,29.9121804 L30.6313369,33.7010805 C30.0100598,33.933564 29.5053599,34.4073151 29.2284892,35.0179038 C28.9516185,35.6284926 28.9253079,36.3257904 29.155357,36.9560902 C29.2367513,37.1878934 29.3573113,37.4035264 29.511628,37.5933143 L29.511628,37.6794257 C29.6485886,37.8504784 29.808486,38.0012037 29.986656,38.1272048 L30.0884477,38.1272048 C30.2680725,38.2486053 30.4618214,38.3469472 30.6652674,38.4199834 L30.7670592,38.4199834 C30.9902375,38.4724725 31.2171439,38.5070241 31.4456706,38.5233171 L31.4456706,38.5233171 L31.8358722,38.5233171 C32.0038363,38.4934837 32.1685902,38.4473459 32.3278655,38.3855389 L45.5607889,33.3910796 L45.5607889,33.3910796 L45.6795459,33.3910796 L56.7409127,38.3855389 L64.5279791,44 C65.1741963,41.6427057 65.156564,39.1487254 64.4770833,36.8010897 L64.4770833,36.8010897 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M60,25.7985437 C62.0867706,27.2397132 63.6834223,29.2781617 64.5779359,31.6432039 L66.286121,35.7208738 L66.286121,35.8228155 C67.1654936,38.6442784 67.2129199,41.6573944 66.4227758,44.5048544 L66.0128114,46 C66.367706,45.8775146 66.7155619,45.7357148 67.0548043,45.5752427 L84,37.6237864 C83.2391176,30.5692241 80.7009963,23.8196479 76.6206406,18 L60,25.7985437 Z"
                      fill="#FFFFFF"
                    ></path>
                    <path
                      d="M19.2200285,40.8503038 C20.2878534,40.0798707 21.6234963,39.760905 22.9308698,39.9641154 C24.2382432,40.1673257 25.4093372,40.8759238 26.1845419,41.9328265 C26.8628689,42.8341794 27.1925397,43.9443907 27.1142706,45.0638151 L27.5537788,45.0638151 C29.1546247,45.0580327 30.6595951,45.8149004 31.5938727,47.0956269 C32.2642774,48.000841 32.5930851,49.1081456 32.5236014,50.2266156 C32.7433555,50.2266156 32.9462054,50.2266156 33.1659595,50.2266156 C34.7621785,50.2396934 36.2589859,50.9924559 37.2060534,52.2584274 C37.8996404,53.1904958 38.2296525,54.3373257 38.1357821,55.4893412 C38.4838781,55.4020585 38.8415534,55.3573111 39.2007441,55.3561076 L39.2007441,55.3561076 C40.6512521,55.3807814 42.0215844,56.0162995 42.9651708,57.1019331 C43.9087572,58.1875667 44.3352403,59.6193562 44.1367585,61.0351881 C43.8856802,62.3021401 43.146619,63.4241804 42.0744511,64.1661768 L41.8039846,64.3493729 L44.9481581,66.5976893 C46.0637715,67.3166612 47.5556506,67.0475092 48.3402767,65.9857123 C49.1249027,64.9239154 48.9266397,63.4424786 47.8894817,62.6173367 L46.1990659,61.451543 C46.7230948,60.7853752 47.2640279,60.0359364 47.7880568,59.2365351 L50.9153262,61.4681972 C51.3355277,61.777257 51.8447243,61.9464239 52.3690838,61.9511689 C53.170176,61.9472494 53.9222722,61.5705873 54.3975829,60.935263 C55.2169091,59.8469869 54.9986922,58.3124024 53.9073623,57.4878446 L50.6955721,55.1895657 L52.2169464,53.1744081 L56.6627401,56.3553593 C57.0844688,56.6595259 57.5938422,56.8229161 58.1164978,56.8216768 C59.2330508,56.8574202 60.2383451,56.159518 60.5798198,55.1115729 C60.9212944,54.0636277 60.5161847,52.9196344 59.5871596,52.30839 L55.1413658,49.1274387 L56.6120277,47.3121315 L62.0720709,51.2092131 C62.4922724,51.5182729 63.0014691,51.6874397 63.5258286,51.6921847 C64.3269208,51.6882653 65.0790169,51.3116031 65.5543276,50.6762789 C66.3214673,49.5905836 66.0744435,48.1008579 64.9964904,47.3121315 L58.8264725,42.8821156 C58.3869643,42.5823401 56.544411,41.54978 55.8513405,41.0668083 L55.3442158,40.7004161 L45.4552829,36.2037834 L33.0983428,40.7337244 C32.7875917,40.8520356 32.4644143,40.9358247 32.1348058,40.9835374 C31.875899,41.0006303 31.6161213,41.0006303 31.3572145,40.9835374 C29.2849466,40.957025 27.4375713,39.6908977 26.6916667,37.7859319 C26.2446691,36.5750522 26.305497,35.2386486 26.8607083,34.0720465 C27.4201109,32.8888254 28.4272953,31.9682663 29.6667986,31.5073004 L38.118878,28.4262744 L37.1046285,27.710144 C33.583995,25.2266021 28.8402383,25.2602252 25.3562381,27.7934149 C24.9403781,27.514403 24.5054717,27.2639954 24.0546179,27.0439762 L7.21807579,19.1498878 L7.01522588,19 C3.03792565,24.7979936 0.623752528,31.497841 0,38.4687539 L13.624752,44.8473106 L19.2200285,40.8503038 Z"
                      fill="#FFFFFF"
                    ></path>
                  </g>
                </g>
              </svg>
              <span>{partnerships.length}</span>
            </div>
            <h2>School Partners</h2>
            <SegmentedControl
              options={['List', 'Map']}
              value={mapShown ? 'Map' : 'List'}
              onChange={(val) => setMapShown(val === 'Map')}
            />
          </SectionHeader>
          {mapShown ? (
            <div>
              <MapDisplay
                locations={[...partnerLocations, partnerLocation]}
                autoZoom
              />
            </div>
          ) : (
            <MapPartnerTable
              programNames={programNames}
              schoolYearId={selectedSchoolYearId}
              partners={partnerships}
              type={Types.SCHOOL_TYPE}
            />
          )}
        </section>
      )}

      {/* Programs */}
      {hasPrograms && (
        <section
          id="partner-programs"
          name="programs"
          className="colored-block blue-block programs-block"
        >
          <div className="content-block-container extra-padding ">
            <SectionHeader>
              <div className="text-block">
                <h2>Programs</h2>
                <p>
                  This organization's arts education program offerings are
                  listed below, along with the arts program types which best
                  define each respective program.
                </p>
              </div>
            </SectionHeader>
            {programGroups.map((group, i) => (
              <div key={i}>
                {group.map((program) => (
                  <ProgramBlock
                    key={program.id}
                    program={program}
                    programPhotosEnabled={partnerProgramPhotoEnabled}
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Artists */}
      {hasArtists && teachingArtist?.enabled && (
        <section name="artists">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <InstructorsIcon />
                <span>{artists.length}</span>
              </div>
              <h2>Associated Teaching Artists</h2>
              <SegmentedControl
                options={['List', 'Map']}
                value={mapArtistShown ? 'Map' : 'List'}
                onChange={(val) => setMapArtistShown(val === 'Map')}
              />
            </SectionHeader>
            {mapArtistShown ? (
              <MapDisplay
                locations={[...artistLocations, partnerLocation]}
                autoZoom
              />
            ) : (
              <div className="cards-container grid-container col-3">
                {artists.map((artist) => (
                  <div
                    className="card artist-card"
                    key={`artist-${artist.teachingArtistId}`}
                  >
                    <ExpandableItem
                      headerComponent={ArtistHeader}
                      artist={artist}
                      disabled={artist?.partnerPrograms?.length === 0}
                    >
                      <ul className="program-list">
                        {artist?.partnerPrograms?.map((program) => (
                          <li
                            key={`artist-${artist.teachingArtistId}-program-${program.id}`}
                          >
                            <Link
                              to={`${TEACHING_ARTIST_ROUTE}/${artist.teachingArtistId}#ta-programs`}
                            >
                              {program.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </ExpandableItem>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </LoadingContainer>
  )
}

PartnerContent.propTypes = propTypes
PartnerContent.defaultProps = defaultProps

export default PartnerContent
